import { useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { CATEGORIES, categoryIcon, categoryLabel } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLocale } from "@/lib/i18n/locale-context";

export function CategoryPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (slugs: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const { lang } = useLocale();
  const text = lang === "en"
    ? { all: "All categories", selected: "categories selected", clear: "Clear categories", choose: "Choose categories", search: "Search categories", none: "No matching category", done: "Done" }
    : lang === "es"
      ? { all: "Todas las categorías", selected: "categorías seleccionadas", clear: "Borrar categorías", choose: "Elegir categorías", search: "Buscar categorías", none: "Ninguna categoría coincide", done: "Listo" }
      : { all: "Alla kategorier", selected: "kategorier valda", clear: "Rensa kategorier", choose: "Välj kategorier", search: "Sök kategori", none: "Ingen kategori matchade", done: "Klar" };
  const [q, setQ] = useState("");
  const Icon = value.length === 1 ? categoryIcon(value[0]) : undefined;

  const list = CATEGORIES.filter((c) => c.label.toLowerCase().includes(q.trim().toLowerCase()));

  const toggle = (slug: string) =>
    onChange(value.includes(slug) ? value.filter((s) => s !== slug) : [...value, slug]);

  const summary =
    value.length === 0
      ? text.all
      : value.length === 1
        ? categoryLabel(value[0])
        : `${value.length} ${text.selected}`;

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-10 min-w-0 flex-1 items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/50 sm:flex-none sm:min-w-[15rem]"
        >
          <span className="flex min-w-0 items-center gap-2">
            {Icon ? <Icon className="h-4 w-4 shrink-0 text-primary" /> : null}
            <span className="truncate">{summary}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        {value.length > 0 && (
          <button
            type="button"
            aria-label={text.clear}
            onClick={() => onChange([])}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-md border border-border text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {value.length > 1 && (
        <div className="flex flex-wrap gap-1.5 sm:ml-1">
          {value.map((slug) => (
            <button
              key={slug}
              type="button"
              onClick={() => toggle(slug)}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
            >
              {categoryLabel(slug)}
              <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-0 overflow-hidden rounded-xl border-ocean-700/50 bg-ocean-900 p-0 text-ocean-50 shadow-2xl">
          <DialogHeader className="border-b border-ocean-700/50 px-5 py-4 text-left">
            <DialogTitle className="font-heading text-base font-semibold text-white">
              {text.choose}
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 pb-3 pt-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ocean-300/70" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={text.search}
                aria-label={text.search}
                className="h-10 rounded-md border-ocean-700 bg-ocean-700/30 pl-9 text-white placeholder:text-ocean-300/60 focus:border-ocean-500 focus:ring-ocean-500"
              />
            </div>
          </div>

          <div className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-3">
            <CategoryRow
              label={text.all}
              active={value.length === 0}
              onClick={() => onChange([])}
            />
            {list.map((c) => (
              <CategoryRow
                key={c.slug}
                label={c.label}
                icon={c.icon}
                active={value.includes(c.slug)}
                onClick={() => toggle(c.slug)}
              />
            ))}
            {list.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-ocean-300/70">
                {text.none}.
              </p>
            )}
          </div>

          <div className="border-t border-ocean-700/50 bg-ocean-900 px-5 py-4">
            <Button
              className="h-10 w-full bg-ocean-300 text-ocean-900 hover:bg-ocean-300/90 active:scale-[0.98]"
              onClick={() => setOpen(false)}
            >
              {text.done}
            </Button>
          </div>

        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryRow({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
        active
          ? "bg-ocean-700/50 font-semibold text-ocean-300"
          : "text-ocean-100/80 hover:bg-ocean-700/30 hover:text-white"
      }`}
    >

      {Icon ? (
        <Icon className={`h-4 w-4 shrink-0 ${active ? "text-ocean-300" : "text-ocean-300/70"}`} />
      ) : (
        <span className="h-4 w-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {active && <Check className="h-4 w-4 shrink-0" />}
    </button>
  );
}
